import { Component, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TagModule } from 'primeng/tag';
import { TabViewModule } from 'primeng/tabview';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CheckboxModule } from 'primeng/checkbox';
import { ToolbarModule } from 'primeng/toolbar';
import { DialogModule } from 'primeng/dialog';

import { MaintainAccountService } from './maintain-account.service';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TabAdditionalNameComponent } from './tabs/tab-additional-name.component';
import { TabEmailsComponent } from './tabs/tab-emails.component';
import { TabAddressesComponent } from './tabs/tab-addresses.component';
import { TabPhoneNumbersComponent } from './tabs/tab-phone-numbers.component';
import { TabPaymentInfoComponent } from './tabs/tab-payment-info.component';
import { TabRelationsComponent } from './tabs/tab-relations.component';
import { TabMoveCodesComponent } from './tabs/tab-move-codes.component';
import { TabMoveCode6111Component } from './tabs/tab-move-code6111.component';
import { TabUrlsComponent } from './tabs/tab-urls.component';
import { TabUploadsComponent } from './tabs/tab-uploads.component';
import { TabProfilersComponent } from './tabs/tab-profilers.component';

import { Pagination } from '../../../services/pagination.abstruct';

import { CreateAccountComponent } from '../create-account/create-account.component';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';

import { TranslateService } from '../../../services/translate.service';
import { DirectionService } from '../../../services/direction.service';

// Corrected imports for User model and all related types
import {
	User,
	ApiUser,
	DataType
} from '../../../models/user.model';

// Define an interface for your column configuration
interface Column {
	field: string;
	header: string;
	sortable?: boolean;
	type?: 'text' | 'boolean' | 'date' | 'tag';
	visibility?: boolean;
}

interface ExpandedRows {
	[key: string]: boolean;
}

@Component({
	selector: 'app-maintain-account',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		TableModule,
		ButtonModule,
		RippleModule,
		ProgressSpinnerModule, // Ensure this is imported
		TagModule,
		TabViewModule,
		CardModule,
		InputTextModule,
		DropdownModule,
		ToastModule,
		TooltipModule,
		SelectButtonModule,
		ConfirmDialogModule,
		HttpClientModule,
		MessageModule,
		CheckboxModule,
		ToolbarModule,
		DialogModule,
		TabAdditionalNameComponent,
		TabEmailsComponent,
		TabAddressesComponent,
		TabPhoneNumbersComponent,
		TabPaymentInfoComponent,
		TabRelationsComponent,
		TabMoveCodesComponent,
		TabMoveCode6111Component,
		TabUrlsComponent,
		TabUploadsComponent,
		TabProfilersComponent,
		CreateAccountComponent
	],
	providers: [MessageService, ConfirmationService],
	templateUrl: './maintain-account.component.html',
	styleUrls: ['./maintain-account.component.scss'],
	changeDetection: ChangeDetectionStrategy.Default // Explicitly set for clarity
})
export class MaintainAccountComponent extends Pagination implements OnInit {
	users: User[] = [];
	selectedUsers: User[] = [];
	expandedRows: ExpandedRows = {};
	isExpanded: boolean = false;
	loading: boolean = false;

	createAccountDialogVisible: boolean = false;

	entityTypeOptions: { label: string; value: 'Customer' | 'Supplier'; id: 100 | 200 }[];

	selectedEntityType: 'Customer' | 'Supplier' = 'Customer';
	selectedEntityTypeId: 100 | 200 = 100;



	searchValue: string = '';
	statusFilter: any = null;
	statusOptions: { label: string; value: boolean | null }[] = [];

	cols: Column[] = [];
	globalFilterFields: string[] = [];
	viewCodes = [{ label: 'Customer View (100)', value: '100' },
	{ label: 'Supplier View (200)', value: '200' }];

	@ViewChild('dt1') dt1!: Table;

	private onTabChangedFunctions: (() => void)[] = [];
	private userDataMappings: { key: keyof User; apiName: string, special?: any }[] = [];
	
	constructor(
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		public translateServ: TranslateService,
		public dirServ: DirectionService,
		private cdr: ChangeDetectorRef,
		private router: Router
	) {
		super();
		console.log('MaintainAccountComponent: Constructor called');
		this.entityTypeOptions = [
			{ label: this.translateServ.dictionary['CUSTOMER'], value: 'Customer', id: 100 },
			{ label: this.translateServ.dictionary['SUPPLIER'], value: 'Supplier', id: 200 }
		];

		this.statusOptions = [
			{ label: this.translateServ.dictionary['MAINT_CS_ACT_SEL_VIEW_ALL'] 	, value: null },
			{ label: this.translateServ.dictionary['MAINT_CS_ACT_SEL_VIEW_ACT'] 	, value: true },
			{ label: this.translateServ.dictionary['MAINT_CS_ACT_SEL_VIEW_INACT']	, value: false }
		];

		this.userDataMappings = this.maintainAccountService.getUserDataMappings();

	}

		ngOnInit(): void {
		console.log('MaintainAccountComponent: ngOnInit called');
		this.initializeColumns();
		this.onEntityTypeChange();
	}

	initializeColumns(): void {
		this.cols = [
			{ field: 'id', 							header: this.translateServ.dictionary['MAINT_CS_TIT_ID'], 			sortable: true	, visibility: true },
			{ field: 'mainName', 					header: this.translateServ.dictionary['MAINT_CS_TIT_NAME'], 		sortable: true	, visibility: true },
			{ field: 'businessId', 					header: this.translateServ.dictionary['MAINT_CS_TIT_BUS_ID'], 		sortable: true	, visibility: true },
			{ field: 'businessType', 				header: this.translateServ.dictionary['MAINT_CS_TIT_BUS_TYPE'], 	sortable: true	, visibility: true },
			{ field: 'opperatingWorker', 			header: this.translateServ.dictionary['MAINT_CS_TIT_OP_WORKER'], 	sortable: true	, visibility: this.isEntityIsCustomer() },
			{ field: 'customer_vat_expences_num', 	header: this.translateServ.dictionary['MAINT_CS_TIT_MAM_TSU'], 		sortable: true	, visibility: this.isEntityIsCustomer()},
			{ field: 'withholdingTaxCode', 			header: this.translateServ.dictionary['MAINT_CS_TIT_WH_TAX'], 		sortable: true	, visibility: this.isEntityIsCustomer()},
			{ field: 'customerStatusText', 			header: this.translateServ.dictionary['MAINT_CS_TIT_CUST_STAT'], 	sortable: true	, visibility: true },
			{ field: 'isActive', 					header: this.translateServ.dictionary['MAINT_CS_TIT_ACT_STAT'], 	sortable: true	, type: 'boolean',  visibility: true },
			{ field: 'dateEntered', 				header: this.translateServ.dictionary['MAINT_CS_TIT_DATE_ENTERED'], sortable: true	, type: 'date', 	visibility: true },
			{ field: 'dateUpdated', 				header: this.translateServ.dictionary['MAINT_CS_TIT_DATE_UPDATED'], sortable: true	, type: 'date', 	visibility: true },
		];

		this.globalFilterFields = this.cols.filter(col => col.field !== 'isActive' && col.field !== 'dateEntered' && col.field !== 'dateUpdated').map(col => col.field);
	}

	onEntityTypeChange(): void {
		const selectedOption = this.entityTypeOptions.find(opt => opt.value === this.selectedEntityType);
		if (selectedOption) {
			this.selectedEntityTypeId = selectedOption.id;
			this.maintainAccountService.setSelectedEntityTypeId(this.selectedEntityTypeId);
			this.initializeColumns(); // Recalculate column visibility
			console.log(`onEntityTypeChange: Selected Entity Type: ${this.selectedEntityType}, ID: ${this.selectedEntityTypeId}`);
			this.messageService.add({
				severity: 'info',
				summary: 'View Changed',
				detail: `Viewing ${this.selectedEntityType} accounts.`
			});
			this.loadUsers(this.selectedEntityTypeId);
		}
	}

	isEntityIsCustomer()
	{
		return this.selectedEntityTypeId == 100;
	}

	loadUsers(typeId: 100 | 200): void {
		console.log(`loadUsers: Started loading users for typeId: ${typeId}...`);
		this.loading = true;
		this.users = [];
		this.expandedRows = {};
		this.isExpanded = false;

		this.maintainAccountService.loadAllUsers(typeId).subscribe({
			next: (apiUsers: ApiUser[]) => {
				console.log('API Response for loadAllUsers (raw):', apiUsers);
				this.users = apiUsers.map(apiUser => ({

					id: apiUser.RecID,
					mainName: apiUser.CustomerName,
					businessId: apiUser.Business_ID,

					opperatingWorker: apiUser.Worker,

					customer_vat_expences_num: apiUser.customer_vat_expences_num,
					withholdingTaxCode 	: apiUser.WithholdingTax,
					withholdingTaxAcc	: apiUser.WithholdingTaxAccount,

					isActive: apiUser.IsActive === 1,
					customerStatusText: apiUser.CustomerStatus,
					businessType: apiUser.BusinessType,
					dateEntered: apiUser.DateEntered,
					dateUpdated: apiUser.DateUpdated,
					photoUrl: null,
					phone: undefined,
					dateCreated: undefined,
					lastLogin: undefined,
					
					additionalNames: undefined,
					additionalNamesLoading: false,
					additionalNamesError: false,
					emails: undefined,
					emailsLoading: false,
					emailsError: false,
					profiles: undefined,
					profilesLoading : false,
					profilesError: false,
					phoneNumbers: undefined,
					phoneNumbersLoading: false,
					phoneNumbersError: false,
					relations: undefined, // Initialize new properties
					relationsLoading: false,
					relationsError: false,
					addresses: undefined,
					addressesLoading: false,
					addressesError: false,
					paymentTypes: undefined,
					paymentTypesLoading: false,
					paymentTypesError: false,
					moveCodes: undefined,
					moveCodesLoading: false,
					moveCodesError: false,
					moveCode6111s: undefined,
					moveCode6111sLoading: false,
					moveCode6111sError: false,
					moveInPrms: undefined,
					moveInPrmsLoading: false,
					moveInPrmsError: false,
					urls: undefined,
					urlsLoading: false,
					urlsError: false,
					docFiles: undefined,
					docFilesLoading: false,
					docFilesError: false,
					uploads: undefined,
					uploadsLoading: false,
					uploadsError: false
				}));
				console.log('Processed Users array (after mapping):', this.users);
				this.loading = false;
				console.log('loadUsers: Users loaded:', this.users.length, 'users');
				console.log('loadUsers: loading state:', this.loading);
				this.cdr.detectChanges(); // Force change detection after users are loaded
			},
			error: (err: HttpErrorResponse) => { // Typed 'err'
				console.error('loadUsers: Error fetching users:', err);
				this.loading = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: `Failed to load ${this.selectedEntityType} accounts.`
				});
				this.cdr.detectChanges(); // Force change detection on error
			}
		});
	}

	editUser(userId: number): void {
		const user = this.users.find(u => u.id === userId);
		if (user) {
			console.log('Editing user:', user.mainName);
			this.messageService.add({
				severity: 'info',
				summary: 'Edit User',
				detail: `Opening edit form for ${user.mainName}`
			});
		}
	}

	toggleUserStatus(userId: number): void {
		const user = this.users.find(u => u.id === userId);
		if (user) {
			const newStatus = !user.isActive;
			this.confirmationService.confirm({
				message: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} ${user.mainName}?`,
				header: 'Confirm Status Change',
				icon: 'pi pi-exclamation-triangle',
				accept: () => {
					// Update the user object in the main 'users' array immutably
					this.users = this.users.map(u => u.id === userId ? { ...u, isActive: newStatus } : u);
					this.updateUserActivity(userId, newStatus ? 1 : 0);
					this.messageService.add({
						severity: 'success',
						summary: 'Status Updated',
						detail: `${user.mainName} is now ${newStatus ? 'active' : 'inactive'}`
					});
					this.cdr.detectChanges(); // Force update after status change
				},
				reject: () => {
					this.messageService.add({
						severity: 'info',
						summary: 'Cancelled',
						detail: 'Status change cancelled'
					});
					this.cdr.detectChanges(); // Force update after rejection
				}
			});
		}
	}

	getSeverity(status: boolean): 'success' | 'danger' | 'info' {
		return status ? 'success' : 'danger';
	}

	getEmailSeverity(status: boolean | undefined): 'success' | 'danger' | 'info' {
		if (status === true) {
			return 'success';
		} else if (status === false) {
			return 'danger';
		} else {
			return 'info';
		}
	}

	getAdditionalNameSeverity(status: boolean | undefined): 'success' | 'danger' | 'info' {
		if (status === true) {
			return 'success';
		} else if (status === false) {
			return 'danger';
		} else {
			return 'info';
		}
	}

	onRowExpand(event: any): void {
		console.log('PrimeNG onRowExpand event triggered for user:', event.data.mainName);
		const user = event.data as User;
		this.expandedRows = { ...this.expandedRows, [user.id]: true };
		this.checkAllRowsExpanded();
		this.debugExpandedRows();

		// Trigger loading for the first tab (Additional Names) when a row expands
		this._loadGeneralDataForUser(user, this.userDataMappings[0].key, this.userDataMappings[0].apiName, this.userDataMappings[0].special);

	}

	onRowCollapse(event: any): void {
		console.log('PrimeNG onRowCollapse event triggered for user:', event.data.mainName);
		const { [event.data.id]: removed, ...rest } = this.expandedRows;
		this.expandedRows = rest;
		this.checkAllRowsExpanded();
		this.debugExpandedRows();
		this.cdr.detectChanges(); // Force update after collapse
	}

	expandAll(): void {
		if (!this.isExpanded) {
			const newExpandedRows: ExpandedRows = {};
			this.users.forEach(user => {
				newExpandedRows[user.id] = true;
				// Trigger loading for the first tab for all expanded users
				this._loadGeneralDataForUser(user, this.userDataMappings[0].key, this.userDataMappings[0].apiName, this.userDataMappings[0].special);
			});
			this.expandedRows = newExpandedRows;
			console.log('All rows expanded');
		} else {
			this.expandedRows = {};
			console.log('All rows collapsed');
		}
		this.isExpanded = !this.isExpanded;
		this.debugExpandedRows();
		this.cdr.detectChanges(); // Force update after expand/collapse all
	}

	private checkAllRowsExpanded(): void {
		const expandedCount = Object.keys(this.expandedRows).length;
		const totalUsers = this.users.length;
		this.isExpanded = expandedCount === totalUsers && totalUsers > 0;
		console.log(`Expanded rows: ${expandedCount}/${totalUsers}, isExpanded: ${this.isExpanded}`);
	}

	debugExpandedRows(): void {
		console.log('Current expandedRows state:', this.expandedRows);
		console.log('Users array IDs:', this.users.map(u => u.id));
	}

	onTabChange(event: any, user: User): void {
		console.log(`Tab changed to index: ${event.index} for user: ${user.mainName}`);
		user.activeTabIndex = event.index; // Set the activeTabIndex for the user
		this.cdr.detectChanges(); // Ensure UI updates

		this._loadGeneralDataForUser(user, this.userDataMappings[event.index].key, this.userDataMappings[event.index].apiName, this.userDataMappings[event.index].special);
	}

	private _loadGeneralDataForUser<K extends keyof User>(user: User, key: K, apiName: string, additions?: any): void {
		if (
			(user as any)[key] === undefined &&
			!(user as any)[`${String(key)}Loading`] &&
			!(user as any)[`${String(key)}Error`]
		) {
			console.log(`Lazy loading ${String(key)} for user ID: ${user.id}`);

			// Mark as loading
			this.users = this.users.map(u =>
				u.id === user.id ? { ...u, [`${String(key)}Loading`]: true, [`${String(key)}Error`]: false } : u
			);
			this.cdr.detectChanges();

			// Fetch data dynamically
			this.maintainAccountService.getDatas<DataType>(user.id, apiName, additions).subscribe({
				next: (data: DataType[]) => {
					this.users = this.users.map(u =>
						u.id === user.id
							? {
								...u,
								[key]: data,
								[`${String(key)}Loading`]: false,
								[`${String(key)}Error`]: false
							}
							: u
					);
					this.cdr.detectChanges();
				},
				error: (err: HttpErrorResponse) => {
					console.error(`Error fetching ${String(key)} for user ${user.id}:`, err);
					this.users = this.users.map(u =>
						u.id === user.id
							? { ...u, [`${String(key)}Error`]: true, [`${String(key)}Loading`]: false }
							: u
					);
					this.messageService.add({
						severity: 'error',
						summary: `${String(key)} Load Error`,
						detail: `Failed to load ${String(key)} for ${user.mainName}.`
					});
					this.cdr.detectChanges();
				}
			});
		} else {
			console.log(
				`${String(key)} for user ${user.id} already loaded or being loaded/errored. Skipping API call.`
			);
		}
	}


	getUploadSeverity(isActive: boolean): string {
		return isActive ? 'success' : 'danger';
	}

	getRelationSeverity(isActive: boolean): string {
		return isActive ? 'success' : 'danger';
	}

	getAddressSeverity(isActive: number): 'success' | 'danger' {
		return isActive === 1 ? 'success' : 'danger';
	}

	getUrlSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	getMoveCode6111Severity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	// Helper for Phone Numbers tag severity
	getPhoneNumberSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	getMoveCodeSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger'; // Assuming 1 is active, 0 is inactive
	}


	onGlobalFilter(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.dt1.filterGlobal(value, 'contains');
	}

	clear(): void {
		this.dt1.clear();
		this.searchValue = '';
		this.statusFilter = null;
		this.cdr.detectChanges(); // Force update after clear
	}

	refreshData(): void {
		this.expandedRows = {};
		this.isExpanded = false;
		this.loadUsers(this.selectedEntityTypeId);
		this.messageService.add({
			severity: 'success',
			summary: 'Refreshed',
			detail: 'User data has been refreshed'
		});
		this.cdr.detectChanges(); // Force update after refresh
	}

	openCreateDialog() {
		this.createAccountDialogVisible = true;
	}

	goToCreateAccountPage() {
		this.createAccountDialogVisible = false;
		this.router.navigate(['/management/create-account']);
	}


	updateUserActivity(accRecID : number,statusToSet: 0 | 1)
	{
		this.maintainAccountService.updateUserActivity(accRecID, this.selectedEntityTypeId, statusToSet);
	}
}